import { Injectable, inject } from '@angular/core';
import {
  ExtendedMap,
  JepGeoJSONLayerProperties
} from '@jeppesen-foreflight/jmkw-types';
import { Observable, of, throwError } from 'rxjs';
import { VECTOR_TILE_ROOT_URL } from '../../data-protocol-services/twc-vector-tile.data.service';
import {
  TwcResponseData,
  TwcUpdateData
} from '../../data-protocol-services/twc.data.types';
import { TwcProduct } from '../../twc-products';
import { TwcTime } from '../../twc-time';
import {
  initialFlightLevel,
  initialTime,
  normalizeSelectedFlightLevel,
  normalizeSelectedTime
} from '../../twc.mixin';
import { TwcLayerServiceInterface } from '../../twc.refresh-layer-plugin.service';
import { TwcGeoJSONLayer } from './twc-geo-json.layer';
import { TWC_CONFIG_TOKEN } from '../../plugins/twc-config.token';

export interface TwcGeoJSONLayerData {
  type: string;
  features?: any[];
}

interface LayerParams {
  time: string;
  apiKey: string;
  declutter: boolean;
}

@Injectable({ providedIn: 'root' })
export class TwcGeoJSONPluginService
  implements TwcLayerServiceInterface<TwcGeoJSONLayer>
{
  private readonly env = inject(TWC_CONFIG_TOKEN);
  private readonly twcApiKey: string;

  constructor() {
    this.twcApiKey = this.env.apiKey ?? '';
    if (!this.twcApiKey) {
      throw new Error(
        'TWC API key is required but not found in environment configuration'
      );
    }
  }

  /**
   * Create a new GeoJSON layer using a dynamic URL.
   * Instead of fetching data via HTTP and creating blob URLs,
   * we provide a static base URL and let the GeoJSONLayer use its
   * built-in dynamic URL mechanism via customParameters and refresh().
   */
  createLayer$({
    timeMap,
    twcProduct,
    flightLevelValues
  }: TwcResponseData): Observable<TwcGeoJSONLayer> {
    if (!twcProduct) {
      return throwError(() => new Error('TWC Product is required'));
    }
    const geoJSONSpecs = twcProduct.layerSpecs?.geoJSON;
    const params: LayerParams = {
      time: initialTime(timeMap),
      apiKey: this.twcApiKey,
      declutter: geoJSONSpecs?.declutter ?? false
    };

    // Build the base URL without any query string.
    const url = this.buildBaseFeatureUrl(twcProduct.defaultProductId);

    // Create a new layer instance and pass the initial custom parameters.
    const newLayer = this.createNewLayer({
      twcProduct,
      url,
      flightLevelValues,
      timeMap,
      geoJSONSpecs,
      customParameters: params
    });

    // Once the layer is loaded, you could add additional behavior (for example,
    // binding to UI controls that update customParameters and then call refresh())
    newLayer.load().then(() => {
      // Layer is loaded and ready.
      // (e.g., add event listeners to update customParameters as in the sample documentation)
    });

    return of(newLayer);
  }

  /**
   * Update the layer by modifying its customParameters and calling refresh().
   * This uses the built-in dynamic URL feature of GeoJSONLayer.
   */
  updateLayer(
    layer: TwcGeoJSONLayer,
    updateData: TwcUpdateData
  ): TwcGeoJSONLayer {
    if (!layer || !updateData.twcProduct) {
      throw new Error('Layer and TWC Product are required for update');
    }

    const selectedFlightLevel = normalizeSelectedFlightLevel(
      updateData.flightLevelValues,
      updateData.selectedFlightLevel,
      layer.selectedFlightLevel
    );
    const selectedTime = normalizeSelectedTime(
      updateData.timeMap,
      updateData.selectedTime,
      layer.selectedTime
    );

    layer.updateFlightLevels(updateData.flightLevelValues, selectedFlightLevel);
    layer.updateTimeMap(updateData.timeMap, selectedTime);

    const geoJSONSpecs = updateData.twcProduct.layerSpecs?.geoJSON;
    const params: LayerParams = {
      time: selectedTime,
      apiKey: this.twcApiKey,
      declutter: geoJSONSpecs?.declutter ?? false
    };

    // Update the customParameters. The GeoJSONLayer implementation will use these
    // parameters to form the new request when refresh() is called.
    layer.customParameters = params as unknown as Record<string, string>;

    try {
      layer.refresh();
      // console.log('Layer refreshed with new parameters', params);
    } catch (error) {
      console.error('Error refreshing GeoJSON layer', error);
    }
    return layer;
  }

  /**
   * Returns the static base URL (without query parameters) used by the GeoJSONLayer.
   */
  private buildBaseFeatureUrl(productId: string): string {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    return `${VECTOR_TILE_ROOT_URL}/${productId}/features`;
  }

  /**
   * Create a new TwcGeoJSONLayer instance.
   * Notice that we now include a customParameters property which the layer
   * will use for dynamically fetching data.
   */
  private createNewLayer({
    twcProduct,
    url,
    flightLevelValues,
    timeMap,
    geoJSONSpecs,
    selectedFlightLevel,
    selectedTime,
    previousInstance = {},
    customParameters
  }: {
    twcProduct: TwcProduct;
    url: string;
    flightLevelValues: string[];
    timeMap: ExtendedMap<TwcTime>;
    geoJSONSpecs?: JepGeoJSONLayerProperties;
    selectedFlightLevel?: string;
    selectedTime?: string;
    previousInstance?: Partial<TwcGeoJSONLayer>;
    customParameters?: any;
  }): TwcGeoJSONLayer {
    if (!twcProduct || !url) {
      throw new Error('Product and URL are required to create new layer');
    }
    return new TwcGeoJSONLayer({
      ...previousInstance,
      id: twcProduct.id,
      zOrder: twcProduct.zOrder,
      url,
      flightLevelValues: flightLevelValues || [],
      selectedFlightLevel:
        selectedFlightLevel || initialFlightLevel(flightLevelValues),
      timeMap,
      selectedTime: selectedTime || initialTime(timeMap),
      label: twcProduct.label,
      renderer: geoJSONSpecs?.renderer,
      customParameters, // now the layer has the dynamic query parameters
      ...(geoJSONSpecs || {})
    });
  }
}
